import { useMemo } from 'react';
import { Batch, Operation, RecordWithProtocol } from '../../types/sharedTypeImpl.ts';
import { NOT_SPECIFIED_MEASUREMENT_VARIANT } from './useRangeOptions.ts';
import { RangeSelection } from './useRangeOptions.ts';

interface Props {
    allRecords: RecordWithProtocol[] | null;
    recordsPerBatch: RecordWithProtocol[][] | null;
    batches: Batch[] | undefined;
    rangeSelection: RangeSelection | null;
    allOperations: Operation[] | null;
    operationsPerBatch: Operation[][] | null;
}

export function useFilterData({ allRecords, recordsPerBatch, batches, rangeSelection, allOperations, operationsPerBatch }: Props) {
    const filteredRecords = useMemo(() => {
        if (!allRecords || !recordsPerBatch || !rangeSelection) return null;

        let records: RecordWithProtocol[];
        if (rangeSelection.rangeType === 'All') {
            records = allRecords;
        } else if (rangeSelection.rangeType === 'Batch') {
            if (rangeSelection.batchIndex == null) return null;
            records = recordsPerBatch[rangeSelection.batchIndex] ?? [];
        } else if (rangeSelection.rangeType === 'Order number') {
            if (!rangeSelection.orderNumber) return null;
            records = allRecords.filter((it) => it.orderNumber === rangeSelection.orderNumber);
        } else if (rangeSelection.rangeType === 'Date') {
            const start = rangeSelection.dateRange?.start;
            const end = rangeSelection.dateRange?.end;
            if (!start && !end) {
                records = allRecords;
            } else if (start && end && start > end) {
                records = [];
            } else {
                const endDateDayEnd = end; // include the whole end day
                if (endDateDayEnd) {
                    endDateDayEnd.setDate(endDateDayEnd.getDate() + 1);
                }
                records = allRecords
                    .filter((it) => it.measurementStart)
                    .filter((it) => !start || it.measurementStart! >= start)
                    .filter((it) => !endDateDayEnd || it.measurementStart! < endDateDayEnd);
            }
        } else {
            return null;
        }

        if (rangeSelection.measurementVariant) {
            if (rangeSelection.measurementVariant === NOT_SPECIFIED_MEASUREMENT_VARIANT) {
                records = records.filter((it) => !it.measurementVariant?.trim());
            } else {
                records = records.filter((it) => it.measurementVariant === rangeSelection.measurementVariant);
            }
        }

        return records;
    }, [allRecords, recordsPerBatch, rangeSelection]);

    const filteredOperations = useMemo(() => {
        if (!allOperations || !operationsPerBatch || !batches || !rangeSelection) return null;

        if (rangeSelection.rangeType === 'All') {
            return allOperations;
        } else if (rangeSelection.rangeType === 'Batch') {
            if (rangeSelection.batchIndex == null) return null;
            return operationsPerBatch[rangeSelection.batchIndex] ?? null;
        } else if (rangeSelection.rangeType === 'Order number') {
            if (!rangeSelection.orderNumber || !filteredRecords || filteredRecords.length === 0) return null;
            const start = filteredRecords[0].measurementStart;
            const end = filteredRecords.at(-1)?.measurementStart;
            if (!start || !end) return null;

            return allOperations
                .filter((it) => it.effectiveTime)
                .filter((it) => it.effectiveTime! >= start)
                .filter((it) => it.effectiveTime! < end);
        } else if (rangeSelection.rangeType === 'Date') {
            const start = rangeSelection.dateRange?.start;
            const end = rangeSelection.dateRange?.end;
            if (!start && !end) return allOperations;
            if (start && end && start > end) return [];

            const endDateDayEnd = end; // include the whole end day
            if (endDateDayEnd) {
                endDateDayEnd.setDate(endDateDayEnd.getDate() + 1);
            }

            return allOperations
                .filter((it) => it.effectiveTime)
                .filter((it) => !start || it.effectiveTime! >= start)
                .filter((it) => !endDateDayEnd || it.effectiveTime! < endDateDayEnd);
        } else {
            return null;
        }
    }, [allOperations, operationsPerBatch, batches, rangeSelection, filteredRecords]);

    return {
        filteredRecords,
        filteredOperations,
    };
}
